import { z } from 'zod';
import { MaskedInput } from '@/components/form/masked-input';
import { Field } from '@/components/form/field';
import { FormError } from '@/components/form/form-error';
import { SwitchField } from '@/components/form/switch-field';
import { useZodForm } from '@/components/form/use-zod-form';
import { ApiError } from '@/lib/api-error';
import { Modal } from '@/components/modal/modal';
import { ModalActions } from '@/components/modal/modal-actions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { moneyToMasked } from '@/lib/masks';
import { decimalToInput, measureInput, moneyInput } from '@/lib/schemas';
import { toast } from '@/lib/toast';
import { type PackageItem, useSavePackage } from '../api/costs';

/**
 * Cadastro de embalagem padrão num modal — na tela de Custos e metas e, como atalho, na tela do
 * produto: quem não acha a medida certa cadastra ali mesmo, sem perder o que estava fazendo.
 */
/**
 * Nome e custo bastam (ADR-054): medida e peso do pacote vêm do ERP, e é por eles que a plataforma
 * cobra o frete. As medidas da caixa são opcionais e vão juntas — só servem para o troig escolher
 * a embalagem sozinho pelo tamanho do produto. Peso não se pede: não entra em conta nenhuma.
 */
const packageSchema = z
  .object({
    name: z.string().trim().min(2, 'Dê um nome à embalagem.').max(80),
    lengthCm: measureInput({ required: false, message: '' }),
    widthCm: measureInput({ required: false, message: '' }),
    heightCm: measureInput({ required: false, message: '' }),
    cost: moneyInput({ required: true }),
    active: z.boolean(),
  })
  .refine(
    (value) =>
      [value.lengthCm, value.widthCm, value.heightCm].every((item) => item === null) ||
      [value.lengthCm, value.widthCm, value.heightCm].every((item) => item !== null),
    { message: 'Informe as três medidas ou deixe as três em branco.', path: ['lengthCm'] },
  );

export function PackageModal({
  value,
  onClose,
  onSaved,
  initial,
}: {
  value: PackageItem | 'new' | null;
  onClose: () => void;
  /** Depois de salvar, com o id da embalagem — a tela do produto a escolhe na hora. */
  onSaved?: (packageId: string) => void;
  /** Medidas sugeridas para a embalagem nova (as do produto, quando o ERP informa). */
  initial?: Partial<Pick<PackageItem, 'lengthCm' | 'widthCm' | 'heightCm'>>;
}) {
  return (
    <Modal
      open={value !== null}
      onOpenChange={(open) => !open && onClose()}
      title={value === 'new' ? 'Nova embalagem' : 'Editar embalagem'}
      description="Medidas internas, em centímetros."
    >
      {/* Montado só com o modal aberto: cada abertura começa do valor certo, sem efeito. */}
      {value !== null && (
        <PackageForm value={value} onClose={onClose} onSaved={onSaved} initial={initial} />
      )}
    </Modal>
  );
}

function PackageForm({
  value,
  onClose,
  onSaved,
  initial,
}: {
  value: PackageItem | 'new';
  onClose: () => void;
  onSaved?: (packageId: string) => void;
  initial?: Partial<Pick<PackageItem, 'lengthCm' | 'widthCm' | 'heightCm'>>;
}) {
  const save = useSavePackage();
  const existing = value === 'new' ? null : value;
  const form = useZodForm(packageSchema, {
    name: existing?.name ?? '',
    lengthCm: decimalToInput(existing?.lengthCm ?? initial?.lengthCm ?? null),
    widthCm: decimalToInput(existing?.widthCm ?? initial?.widthCm ?? null),
    heightCm: decimalToInput(existing?.heightCm ?? initial?.heightCm ?? null),
    cost: moneyToMasked(existing?.cost ?? null),
    active: existing?.active ?? true,
  });

  const submit = form.handleSubmit(async ({ lengthCm, widthCm, heightCm, cost, ...rest }) => {
    if (!cost) return;
    const saved = await save
      .mutateAsync({
        ...(existing ? { id: existing.id } : {}),
        ...rest,
        lengthCm,
        widthCm,
        heightCm,
        // Campo fora do formulário (ADR-054): salvar limpa o que havia.
        cost,
      })
      // Editar responde sem corpo; criar devolve o id da embalagem nova.
      .then((result) => ({ id: existing?.id ?? result?.id }))
      .catch(() => null);
    if (!saved) return;
    onClose();
    if (onSaved && saved.id) onSaved(saved.id);
    else toast.success('Embalagem salva. As margens estão sendo recalculadas.');
  });

  // Medidas repetidas (PACKAGE_DUPLICATE): o servidor diz qual embalagem já existe.
  const existingMatch =
    save.error instanceof ApiError && save.error.code === 'PACKAGE_DUPLICATE'
      ? (save.error.details?.existing as { id: string; name: string } | undefined)
      : undefined;
  const control = form.control;
  return (
    <form onSubmit={submit} className="grid grid-cols-3 gap-3" noValidate>
      <FormError error={save.error} fallback="Não foi possível salvar." className="col-span-3" />
      {existingMatch && onSaved && (
        // No produto, a embalagem com as mesmas medidas serve: usa a existente em vez de repetir.
        <div className="col-span-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              onClose();
              onSaved(existingMatch.id);
            }}
          >
            Usar {existingMatch.name}
          </Button>
        </div>
      )}
      <Field control={control} name="name" label="Nome" required className="col-span-3">
        {(field) => <Input {...field} />}
      </Field>
      <Field
        control={control}
        name="cost"
        label="Custo (R$)"
        required
        hint="Caixa + enchimento + fita, por pacote"
        className="col-span-3"
      >
        {(field) => <MaskedInput {...field} mask="money" />}
      </Field>
      <p className="col-span-3 text-xs text-muted-foreground">
        Medidas internas (cm), opcionais: só para o troig escolher esta embalagem sozinho pelo
        tamanho do produto. Sem elas, escolha a embalagem na página do produto.
      </p>
      <Field control={control} name="lengthCm" label="Comprimento">
        {(field) => <MaskedInput {...field} mask="measure" />}
      </Field>
      <Field control={control} name="widthCm" label="Largura">
        {(field) => <MaskedInput {...field} mask="measure" />}
      </Field>
      <Field control={control} name="heightCm" label="Altura">
        {(field) => <MaskedInput {...field} mask="measure" />}
      </Field>
      <SwitchField
        control={control}
        name="active"
        className="col-span-3"
        label="Ativa"
        description="Inativa deixa de ser escolhida, mas o histórico fica."
      />
      <div className="col-span-3 flex justify-end gap-2">
        <ModalActions
          onCancel={onClose}
          confirmType="submit"
          confirmLabel="Salvar"
          isLoading={save.isPending}
        />
      </div>
    </form>
  );
}
